"use client";

import type { AuthProvider } from "@refinedev/core";
import Cookies from "js-cookie";
import { captureException } from "@/lib/error-tracker";
import {
  setTokens,
  clearTokens,
  getAccessToken,
  getRefreshToken,
  isTokenExpired,
} from "@/lib/httpClient";

const API_URL = process.env.NEXT_PUBLIC_API_URL || "http://localhost:3200/api";

// User info storage
const USER_INFO_KEY = "user_info";

export interface UserInfo {
  code: string;
  user: string;
  firstname: string;
  lastname: string;
  role: string;
  avatar?: string;
}

const setUserInfo = (userInfo: UserInfo) => {
  Cookies.set(USER_INFO_KEY, JSON.stringify(userInfo), {
    expires: 180, // 6 months
    path: "/",
  });
};

export const getUserInfo = (): UserInfo | null => {
  const userInfo = Cookies.get(USER_INFO_KEY);
  if (userInfo) {
    try {
      return JSON.parse(userInfo);
    } catch {
      return null;
    }
  }
  return null;
};

const clearUserInfo = () => {
  Cookies.remove(USER_INFO_KEY, { path: "/" });
};

const fetchUserProfile = async (accessToken: string): Promise<UserInfo | null> => {
  try {
    const response = await fetch(`${API_URL}/profile`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
      },
    });

    if (!response.ok) {
      captureException(new Error("Failed to fetch user profile"), {
        extra: {
          context: "Profile fetch failed",
          status: response.status,
        },
      });
      return null;
    }

    const profileData = await response.json();
    return profileData;
  } catch (error) {
    captureException(error, {
      extra: { context: "Profile fetch exception" },
    });
    return null;
  }
};

// Track last 401 error log to prevent spam
let last401ErrorTime = 0;
const ERROR_LOG_COOLDOWN_MS = 5000; // Only log 401 errors once every 5 seconds

export const authProviderClient: AuthProvider = {
  login: async ({ username, password, remember }) => {
    try {
      // Call backend login endpoint
      const response = await fetch(`${API_URL}/oauth/token`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          username,
          password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));

        captureException(new Error("Login failed"), {
          extra: {
            context: "Login request failed",
            status: response.status,
            errorData,
          },
        });

        return {
          success: false,
          error: {
            name: "LoginError",
            message: errorData.message || "Invalid username or password",
          },
        };
      }

      const data = await response.json();

      // Store tokens
      setTokens(data.access_token, data.refresh_token, data.expires_in);

      // Fetch user profile
      const userProfile = await fetchUserProfile(data.access_token);

      if (!userProfile) {
        // Failed to fetch profile, but login succeeded - clear tokens and return error
        clearTokens();
        return {
          success: false,
          error: {
            name: "ProfileError",
            message: "Failed to fetch user profile. Please try again.",
          },
        };
      }

      // Store user info
      setUserInfo(userProfile);

      return {
        success: true,
        redirectTo: "/",
      };
    } catch (error) {
      captureException(error, {
        extra: {
          context: "Login exception",
          username,
        },
      });

      return {
        success: false,
        error: {
          name: "LoginError",
          message: "An error occurred during login. Please try again.",
        },
      };
    }
  },

  logout: async () => {
    try {
      const refreshToken = getRefreshToken();

      if (refreshToken) {
        // Call backend logout endpoint to revoke refresh token
        await fetch(`${API_URL}/oauth/logout`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            refresh_token: refreshToken,
          }),
        }).catch((error) => {
          // Log but don't fail logout on error
          captureException(error, {
            extra: { context: "Logout endpoint failed" },
          });
        });
      }
    } finally {
      // Always clear local tokens and user info
      clearTokens();
      clearUserInfo();
    }

    return {
      success: true,
      redirectTo: "/login",
    };
  },

  check: async () => {
    const accessToken = getAccessToken();
    const userInfo = getUserInfo();

    // Check if we have both access token and user info
    if (!accessToken || !userInfo) {
      return {
        authenticated: false,
        logout: true,
        redirectTo: "/login",
      };
    }

    // Check if token is expired
    if (isTokenExpired()) {
      const refreshToken = getRefreshToken();

      // If no refresh token, user needs to login
      if (!refreshToken) {
        clearTokens();
        clearUserInfo();
        return {
          authenticated: false,
          logout: true,
          redirectTo: "/login",
        };
      }

      // Token will be refreshed automatically by httpClient when needed
      // For now, just check if refresh token exists
    }

    return {
      authenticated: true,
    };
  },

  getPermissions: async () => {
    const userInfo = getUserInfo();
    return userInfo?.role || null;
  },

  getIdentity: async () => {
    const userInfo = getUserInfo();

    if (!userInfo) {
      return null;
    }

    // Use firstname + lastname if user has, otherwise use the username
    const hasNames = userInfo.firstname && userInfo.lastname;
    const displayName = hasNames
      ? `${userInfo.firstname} ${userInfo.lastname}`.trim()
      : userInfo.user;

    return {
      id: userInfo.code,
      name: displayName,
      username: userInfo.user,
      firstname: userInfo.firstname,
      lastname: userInfo.lastname,
      role: userInfo.role,
      avatar: userInfo.avatar,
    };
  },

  onError: async (error) => {
    if (error.response?.status === 401 || error.statusCode === 401) {
      // Check if this is an authorization error (user lacks permissions) vs authentication error (token expired)
      const isAuthorizationError = error.message?.includes("no access") ||
                                   error.message?.includes("permissions") ||
                                   error.message?.includes("User has no access");

      if (isAuthorizationError) {
        // User is authenticated but lacks permissions for this action
        // Only log if enough time has passed since last 401 error
        const now = Date.now();
        if (now - last401ErrorTime > ERROR_LOG_COOLDOWN_MS) {
          last401ErrorTime = now;
          captureException(error, {
            extra: {
              context: "401 Authorization - User lacks permissions",
              status: error.response?.status,
              statusCode: error.statusCode,
            },
          });
        }

        return {
          error: {
            name: "AuthorizationError",
            message: "No tienes autorización para realizar esta acción o acceder a este recurso",
          },
        };
      }

      // Authentication error - token expired, clear tokens and redirect to login
      clearTokens();
      clearUserInfo();

      // Only log if enough time has passed since last 401 error
      const now = Date.now();
      if (now - last401ErrorTime > ERROR_LOG_COOLDOWN_MS) {
        last401ErrorTime = now;
        captureException(error, {
          extra: {
            context: "401 Authentication - Token expired or invalid",
            status: error.response?.status,
            statusCode: error.statusCode,
          },
        });
      }

      return {
        logout: true,
        redirectTo: "/login",
        error: {
          name: "SessionExpired",
          message: "Tu sesión ha expirado. Por favor, inicia sesión nuevamente para continuar",
        },
      };
    }

    // Log non-401 errors as they are unexpected
    captureException(error, {
      extra: {
        context: "Authentication error (non-401)",
        status: error.response?.status,
        statusCode: error.statusCode,
      },
    });

    return { error };
  },
};
